import {BetaLabel} from '@github-ui/lifecycle-labels/beta'
import {testIdProps} from '@github-ui/test-id-props'
import {useDebounce} from '@github-ui/use-debounce'
import {PencilIcon} from '@primer/octicons-react'
import {Button, FormControl, Heading, IconButton, TextInput} from '@primer/react'
import {Dialog} from '@primer/react/deprecated'
import {type RefObject, useCallback, useRef, useState} from 'react'

import {isAutoAssignAgentWorkflow} from '../../helpers/workflow-utilities'
import {validateWorkflowName} from '../../helpers/workflow-validation'
import {useAutomationGraph} from '../../state-providers/workflows/use-automation-graph'
import {useWorkflows} from '../../state-providers/workflows/use-workflows'
import {SEARCH_DEBOUNCE_DELAY_MS} from './helpers/search-constants'
import styles from './workflow-name.module.css'

export const WorkflowName = () => {
  const [isEditingName, setIsEditingName] = useState(false)
  const {workflowName, isEditing, workflow} = useAutomationGraph()
  const editButtonRef = useRef<HTMLButtonElement>(null)

  return (
    <>
      <div className={styles.headerContainer}>
        <Heading
          as="h2"
          id="workflow-name"
          className={styles.workflowNameHeading}
          {...testIdProps('workflow-name-heading')}
        >
          {workflowName}
        </Heading>
        {isAutoAssignAgentWorkflow(workflow) && <BetaLabel {...testIdProps('workflow-preview-label')} />}
        {!isEditing && (
          <IconButton
            ref={editButtonRef}
            icon={PencilIcon}
            onClick={() => setIsEditingName(true)}
            variant="invisible"
            aria-label="Edit workflow name"
            {...testIdProps('workflow-name-edit-button')}
          />
        )}
      </div>
      <WorkflowNameEditorDialog isOpen={isEditingName} setIsOpen={setIsEditingName} returnFocusRef={editButtonRef} />
    </>
  )
}

const WorkflowNameEditorDialog = ({
  isOpen,
  setIsOpen,
  returnFocusRef,
}: {
  isOpen: boolean
  setIsOpen: React.Dispatch<boolean>
  returnFocusRef: RefObject<HTMLButtonElement | null>
}) => {
  const {workflowNames} = useWorkflows()

  const {workflowName, saveNameChange} = useAutomationGraph()
  const [localWorkflowName, setLocalWorkflowName] = useState<string>(workflowName)
  const [error, setError] = useState<string | undefined>()

  const inputRef = useRef<HTMLInputElement>(null)

  const validate = useCallback(
    (name: string) => {
      const validationResult = validateWorkflowName(name, workflowNames)
      if (!validationResult.isValid) {
        setError(validationResult.errorMessage)
      } else if (error) {
        setError(undefined)
      }
    },
    [workflowNames, error],
  )

  const debouncedValidation = useDebounce(validate, SEARCH_DEBOUNCE_DELAY_MS)
  const onWorkflowNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newWorkflowName = event.target.value
      setLocalWorkflowName(newWorkflowName)
      debouncedValidation(newWorkflowName)
    },
    [debouncedValidation],
  )

  const handleNameChange = useCallback(() => {
    if (error) return

    setIsOpen(false)
    saveNameChange(localWorkflowName)
  }, [error, localWorkflowName, saveNameChange, setIsOpen])

  const closeDialog = useCallback(() => {
    setLocalWorkflowName(workflowName)
    setIsOpen(false)
    setError(undefined)
  }, [setIsOpen, workflowName])

  return (
    <Dialog
      isOpen={isOpen}
      initialFocusRef={inputRef}
      returnFocusRef={returnFocusRef}
      onDismiss={closeDialog}
      aria-labelledby="workflow-name-editor-header"
      key={isOpen ? 'open' : 'closed'}
    >
      <Dialog.Header id="workflow-name-editor-header" className={styles.dialogHeader}>
        <Heading as="h3" className={styles.dialogTitle}>
          Edit workflow name
        </Heading>
      </Dialog.Header>
      <div className={styles.dialogFormRow}>
        <FormControl className={styles.dialogFormControl}>
          <FormControl.Label>Workflow name</FormControl.Label>
          <TextInput
            ref={inputRef}
            name="workflowName"
            value={localWorkflowName}
            onChange={onWorkflowNameChange}
            validationStatus={error ? 'error' : undefined}
            aria-describedby="workflow-name-editor-error"
            className={styles.dialogTextInput}
            {...testIdProps('workflow-name-editor-input')}
          />
          {error && (
            <FormControl.Validation variant="error" id="workflow-name-editor-error">
              {error}
            </FormControl.Validation>
          )}
        </FormControl>
      </div>
      <div className={styles.dialogActionsRow}>
        <Button variant="default" onClick={closeDialog}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleNameChange}>
          Save
        </Button>
      </div>
    </Dialog>
  )
}
